import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {CalendarDays, PencilLine, RefreshCw, Trash2} from 'lucide-react';
import {useEffect, useState} from 'react';
import {apiRequest} from '../api';
import {Modal, QuietButton, SecondaryButton} from '../design';
import {LoadingPanel, formatDateTime} from './PagePrimitives';
import {CalendarProposalReview} from './CalendarProposalReview';

type ManagedCalendarEvent = {
  id: string;
  summary: string;
  description: string;
  start: {dateTime: string; timeZone?: string};
  end: {dateTime: string; timeZone?: string};
  visibility: 'private';
};

type ProposalResult = {
  proposal: {id: string};
  reused: boolean;
};

export const CalendarEventManager = ({opened, onClose}: {opened: boolean; onClose: () => void}) => {
  const queryClient = useQueryClient();
  const [proposalId, setProposalId] = useState<string | null>(null);
  const events = useQuery({
    queryKey: ['calendar-owned-events'],
    queryFn: () => apiRequest<{items: ManagedCalendarEvent[]}>('/calendar/events'),
    enabled: opened,
  });
  const prepare = useMutation({
    mutationFn: ({eventId, operation}: {eventId: string; operation: 'update' | 'delete'}) =>
      apiRequest<ProposalResult>('/calendar/events/' + encodeURIComponent(eventId) + '/proposals', {
        method: 'POST',
        body: JSON.stringify({operation}),
      }),
    onSuccess: async ({proposal}) => {
      setProposalId(proposal.id);
      await queryClient.invalidateQueries({queryKey: ['calendar-proposals']});
    },
  });

  useEffect(() => {
    if (!opened) setProposalId(null);
  }, [opened]);

  const closeAll = () => {
    setProposalId(null);
    onClose();
  };

  return (
    <>
      <Modal
        opened={opened && proposalId === null}
        onClose={closeAll}
        title="Manage private calendar events"
        centered
        size="xl"
      >
        <div className="pd-calendar-manager">
          <div className="pd-review-banner">
            <CalendarDays size={20} aria-hidden="true" />
            <div>
              <strong>Only events created by PursuitDeck</strong>
              <p>Updates and cancellations always open a separate editable review. No attendee is ever added or notified.</p>
            </div>
            <QuietButton
              aria-label="Refresh private Calendar events"
              leftSection={<RefreshCw size={14} aria-hidden="true" />}
              loading={events.isFetching}
              onClick={() => void events.refetch()}
            >
              Refresh
            </QuietButton>
          </div>

          {events.isLoading ? <LoadingPanel label="Loading private Calendar events" /> : null}
          {events.isError ? (
            <div className="pd-form-error" role="alert">
              <p>{events.error instanceof Error ? events.error.message : 'Private Calendar events could not be loaded.'}</p>
              <QuietButton onClick={() => void events.refetch()}>Try again</QuietButton>
            </div>
          ) : null}
          {prepare.isError ? (
            <p className="pd-form-error" role="alert">
              {prepare.error instanceof Error ? prepare.error.message : 'The Calendar proposal could not be prepared.'}
            </p>
          ) : null}

          {events.data && events.data.items.length === 0 ? (
            <p className="pd-empty-copy">No private PursuitDeck events were found in the current Calendar window.</p>
          ) : null}
          {events.data?.items.length ? (
            <div className="pd-calendar-event-list" role="list">
              {events.data.items.map((event) => {
                const busy = prepare.isPending && prepare.variables?.eventId === event.id;
                return (
                  <article key={event.id} className="pd-calendar-event-row" role="listitem">
                    <div>
                      <strong>{event.summary}</strong>
                      <span>{formatDateTime(event.start.dateTime)} – {formatDateTime(event.end.dateTime)}</span>
                      {event.description ? <p>{event.description}</p> : null}
                    </div>
                    <div className="pd-calendar-event-actions">
                      <SecondaryButton
                        leftSection={<PencilLine size={14} aria-hidden="true" />}
                        loading={busy && prepare.variables?.operation === 'update'}
                        disabled={busy}
                        onClick={() => prepare.mutate({eventId: event.id, operation: 'update'})}
                      >
                        Review update
                      </SecondaryButton>
                      <QuietButton
                        color="red"
                        leftSection={<Trash2 size={14} aria-hidden="true" />}
                        loading={busy && prepare.variables?.operation === 'delete'}
                        disabled={busy}
                        onClick={() => prepare.mutate({eventId: event.id, operation: 'delete'})}
                      >
                        Review cancellation
                      </QuietButton>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : null}
        </div>
      </Modal>
      <CalendarProposalReview
        proposalId={proposalId}
        opened={opened && proposalId !== null}
        onClose={() => setProposalId(null)}
      />
    </>
  );
};
